import React, { FC, useMemo, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import _map from 'lodash/map';
import _filter from 'lodash/filter';
import _find from 'lodash/find';
import _includes from 'lodash/includes';
import _without from 'lodash/without';
import { gqlWillingnessEnum as WILLINGNESS } from '@minecraft.graphql-types';
import { useDrawerSearchFilterNameOrCategory, useDrawerFiltersCounts, useDrawerGlobalLoader } from '@minecraft.drawer';
import ErrorMessage from '@minecraft.error-message';
import { useTranslation } from '@blocs.i18n';
import { FiltersNameEnum, WillingToEnum, FilterDrawerType, FormItem, useFiltersLabel } from '@blocs.filter';
import { FiltersCategoryNameEnum, FiltersCountNameEnum } from '../../shared/types';
import { willingnessesWhiteList } from '../../shared/config';
import { RolePreferences } from '../../FilterDrawerCollection';
import { QUERY_ROLE_PREFERENCES } from './RolePreferencesCategoryGraphQL';

type Props = {
  type: FilterDrawerType;
};

const RolePreferencesCategory: FC<Props> = ({ type }) => {
  const filtersLabelOfAudience = useFiltersLabel(type);
  const { t } = useTranslation();

  const { data, error, loading } = useQuery(QUERY_ROLE_PREFERENCES);

  const { isCategoryMatchedSearch, assignOptionsToCategoryFilters } = useDrawerSearchFilterNameOrCategory(
    FiltersCategoryNameEnum.rolePreferences
  );
  const { filtersCounts } = useDrawerFiltersCounts();

  const rolePreferencesOptions = useMemo(() => {
    const allowedWillingnesses =
      type === FilterDrawerType.Reps
        ? _without(willingnessesWhiteList, WILLINGNESS.EXTRAS_WORK)
        : willingnessesWhiteList;
    const allowedRolePreferences = _filter(data?.rolePreferences, (rolePreference) =>
      _includes(allowedWillingnesses, rolePreference.code)
    );
    const willingToChangeCount =
      _find(filtersCounts?.[FiltersCountNameEnum.willingnessIds], {
        key: WillingToEnum.hairColor,
      })?.docCount || 0;

    return _map(allowedWillingnesses, (item) => {
      const allowedRolePreference = _find(allowedRolePreferences, { code: item });

      if (item === WILLINGNESS.EXTRAS_WORK) {
        return {
          label: t('common:label.willingToDoExtraWork'),
          value: allowedRolePreference?.id,
          count: willingToChangeCount,
        };
      }

      return {
        label: allowedRolePreference?.name,
        value: allowedRolePreference?.id,
        count:
          _find(filtersCounts?.[FiltersCountNameEnum.willingnessIds], { key: allowedRolePreference?.id })?.docCount ||
          0,
      };
    });
  }, [data, filtersCounts, t]);

  useDrawerGlobalLoader(loading);

  useEffect(() => {
    if (rolePreferencesOptions.length > 0) {
      assignOptionsToCategoryFilters({
        [FiltersNameEnum.rolePreferences]: rolePreferencesOptions,
      });
    }
  }, [rolePreferencesOptions]);

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    isCategoryMatchedSearch && (
      <FormItem $fullWidth $marginValue={2}>
        <RolePreferences
          name={FiltersNameEnum.rolePreferences}
          label={filtersLabelOfAudience[FiltersNameEnum.rolePreferences]}
        />
      </FormItem>
    )
  );
};

export default RolePreferencesCategory;
